import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/**
 * Keyboard shortcut display. Pass either [keys] array or ng-content.
 */
@Component({
  selector: 'ds-kbd',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [':host { display: inline-flex; align-items: center; gap: 0.25rem; }'],
  template: `
    @if (keys.length) {
      @for (k of keys; track $index; let last = $last) {
        <kbd [class]="kbdClasses">{{ displayKey(k) }}</kbd>
        @if (!last) {
          <span class="text-token-caption text-warm-hint">{{ separator }}</span>
        }
      }
    } @else {
      <kbd [class]="kbdClasses"><ng-content /></kbd>
    }
  `,
})
export class DsKbdComponent {
  @Input() keys: string[] = [];
  @Input() size: 'sm' | 'md' = 'md';
  @Input() separator = '+';

  get kbdClasses(): string {
    const sz =
      this.size === 'sm'
        ? 'text-token-label px-1.5 py-0.5 min-w-density-badge h-density-badge'
        : 'text-token-caption px-2 py-0.5 min-w-density-badge-md h-density-badge-md';
    // Carot ERP v2 (ds.jsx :: Kbd): nền card, viền hairline, bo xs, chữ `f.mono` cỡ nhỏ, KHÔNG
    // đậm. `f.mono` của tokens.jsx là Mona Sans tabular = `font-mono` ở kho này — KHÔNG phải
    // `font-code` (IBM Plex Mono): ảnh 1280 cho thấy Plex làm "Ctrl K" rộng hơn hẳn thiết kế.
    // Token tự đổi sáng/tối nên không cần `dark:` thủ công.
    return `${sz} inline-flex items-center justify-center rounded-xs border border-warm-border bg-warm-card text-warm-text font-mono font-normal`;
  }

  displayKey(k: string): string {
    const map: Record<string, string> = {
      cmd: '⌘',
      meta: '⌘',
      ctrl: 'Ctrl',
      alt: '⌥',
      opt: '⌥',
      shift: '⇧',
      enter: '↵',
      return: '↵',
      esc: 'Esc',
      escape: 'Esc',
      tab: '⇥',
      up: '↑',
      down: '↓',
      left: '←',
      right: '→',
      space: '␣',
      backspace: '⌫',
      delete: '⌦',
    };
    return map[k.toLowerCase()] ?? k.toUpperCase();
  }
}
